import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import * as FaIcons from "react-icons/fa";
import { FaSun, FaMoon, FaTimes, FaBars, FaBuilding } from "react-icons/fa";
import logo from "../Assets/Images/HD Quality Logo.svg";
import logoDark from "../Assets/Images/HD Quality Logo 2.svg";
import ContactForm from "../ContactForm/ContactFormModal.js";
import { useTheme } from "../../context/ThemeContext.js";

// Helper to lookup react icon dynamically from string name
const getMenuIcon = (iconName) => {
  if (!iconName) return FaIcons.FaGlobe;
  if (typeof iconName !== 'string') return iconName;
  const IconComponent = FaIcons[iconName];
  if (IconComponent) return IconComponent;

  if (iconName.toLowerCase().includes('calculator')) return FaIcons.FaCalculator;
  if (iconName.toLowerCase().includes('chart') || iconName.toLowerCase().includes('trend') || iconName.toLowerCase().includes('line')) return FaIcons.FaChartLine;
  if (iconName.toLowerCase().includes('building') || iconName.toLowerCase().includes('formation')) return FaIcons.FaBuilding;
  if (iconName.toLowerCase().includes('shield') || iconName.toLowerCase().includes('check')) return FaIcons.FaShieldAlt;
  if (iconName.toLowerCase().includes('user') || iconName.toLowerCase().includes('cfo')) return FaIcons.FaUserTie;

  return FaIcons.FaGlobe;
};

const NavigationBar = () => {
  const { theme, toggleTheme } = useTheme();
  const [showDropdownServices, setShowDropdownServices] = useState(false);
  const [showDropdownIndustry, setShowDropdownIndustry] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
        const response = await fetch(`${apiBase}/api/cms/settings`);
        const result = await response.json();
        if (result.success && result.data) {
          setSettings(result.data);
        }
      } catch (error) {
        console.error("Failed to load navigation settings:", error);
      }
    };
    fetchSettings();
  }, []);

  const menuData = settings?.menu || {};
  const logoData = settings?.logo || {};
  const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
  
  const logoUrl = logoData.menuLogoLight
    ? (logoData.menuLogoLight.startsWith('http') ? logoData.menuLogoLight : `${apiBase}/${logoData.menuLogoLight}`)
    : logo;

  const logoDarkUrl = logoData.menuLogoDark
    ? (logoData.menuLogoDark.startsWith('http') ? logoData.menuLogoDark : `${apiBase}/${logoData.menuLogoDark}`)
    : logoDark;

  const logoAlt = logoData.logoAlt || "BooksComply Logo";

  const defaultMainLinks = {
    pricing: { label: "Pricing", path: "/pricing", show: true },
    blog: { label: "Blog", path: "/blog", show: true },
    whyBooksComply: { label: "Why BooksComply", path: "/why-bookscomply", show: true },
    login: { label: "Login", show: true },
    getInTouch: { label: "Get in Touch", show: true },
  };

  const mergeMainLink = (key) => {
    const fromCms = menuData.mainLinks?.[key] || {};
    const fallback = defaultMainLinks[key];
    return {
      ...fallback,
      ...fromCms,
      show: fromCms.show !== false,
    };
  };

  const pricingLink = mergeMainLink('pricing');
  const blogLink = mergeMainLink('blog');
  const whyLink = mergeMainLink('whyBooksComply');
  const loginLink = mergeMainLink('login');
  const getInTouchLink = mergeMainLink('getInTouch');

  const servicesItems = menuData.servicesItems?.length
    ? menuData.servicesItems.map(item => ({
      label: item.label,
      path: item.path,
      icon: getMenuIcon(item.iconName)
    }))
    : [
      { label: "USA Company Formation", icon: FaIcons.FaBuilding, path: "/usa-company-formation" },
      { label: "Tax Return", icon: FaIcons.FaBuilding, path: "/tax-return" },
      { label: "Bookkeeping", icon: FaIcons.FaCalculator, path: "/bookkeeping" },
      { label: "CFO Advisory", icon: FaIcons.FaChartLine, path: "/cfo-advisory" },
      { label: "Zoho Advisory", icon: FaIcons.FaChartLine, path: "/zoho-advisory" },
    ];

  const industryItems = menuData.industryItems?.length
    ? menuData.industryItems.map(item => ({
      label: item.label,
      path: item.path,
      icon: getMenuIcon(item.iconName)
    }))
    : [
      {
        label: "Startup Accounting, Tax & Compliance",
        icon: FaIcons.FaBuilding,
        path: "/industry/startup-accounting-tax-compliance",
      },
      {
        label: "E-Commerce Accounting & Tax",
        icon: FaIcons.FaCalculator,
        path: "/industry/ecommerce-accounting-tax-services",
      },
    ];

  const handleGetStartedClick = (e) => {
    e.preventDefault();
    setShowContactForm(true);
  };

  const handleCloseContactForm = () => {
    setShowContactForm(false);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setShowDropdownIndustry(false);
  };

  return (
    <>
      <nav
        className="sticky top-0 z-[1020] bg-white dark:bg-[#0B0F19] shadow-sm py-1.5 md:py-2 border-b dark:border-white/5 font-['Inter','Poppins','Segoe_UI',-apple-system,BlinkMacSystemFont,'Roboto','Helvetica_Neue',sans-serif]"
        aria-label="Main Navigation"
        role="navigation"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <p className="sr-only" role="heading" aria-level="2">
              BooksComply - Main Navigation
            </p>

            <Link
              className="flex-shrink-0"
              to="/"
              onClick={closeMobileMenu}
              aria-label="BooksComply Home"
            >
              <img
                src={theme === "dark" ? logoDarkUrl : logoUrl}
                alt={logoAlt}
                className="hidden md:block w-auto h-[60px] object-contain"
                height="60"
                fetchPriority="high"
              />
              <img
                src={theme === "dark" ? logoDarkUrl : logoUrl}
                alt={logoAlt}
                className="md:hidden w-auto h-[48px] object-contain"
                height="48"
                fetchPriority="high"
              />
            </Link>

            {/* Mobile Actions Container (Theme Toggle & Hamburger menu) */}
            <div className="flex items-center gap-2 lg:hidden ml-auto">
              <button
                onClick={toggleTheme}
                className="relative w-14 h-7 bg-gray-100 dark:bg-[#1E293B] rounded-full p-1 cursor-pointer transition-colors duration-300 focus:outline-none border border-gray-200 dark:border-white/10 flex items-center select-none"
                aria-label="Toggle Theme"
              >
                <div
                  className={`w-5 h-5 rounded-full shadow-md transform transition-transform duration-300 flex items-center justify-center ${
                    theme === "dark" ? "translate-x-[26px] bg-blue-500 text-white" : "translate-x-0 bg-white text-yellow-500"
                  }`}
                >
                  {theme === "dark" ? (
                    <FaMoon className="w-3 h-3" />
                  ) : (
                    <FaSun className="w-3.5 h-3.5" />
                  )}
                </div>
              </button>

              <button
                className="p-2 border-0 bg-transparent"
                type="button"
                onClick={toggleMobileMenu}
                aria-expanded={mobileMenuOpen}
                aria-label="Toggle navigation"
              >
                {mobileMenuOpen ? (
                  <FaTimes className="text-[#131738] dark:text-slate-200 text-2xl" />
                ) : (
                  <FaBars className="text-[#131738] dark:text-slate-200 text-2xl" />
                )}
              </button>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex lg:items-center lg:ml-auto lg:min-w-0 lg:flex-1 lg:justify-end lg:gap-2 xl:gap-4">
              <ul className="flex items-center flex-wrap justify-end gap-x-3 xl:gap-x-6 gap-y-1 mb-0 max-w-full" role="menubar" aria-label="Main Menu">
                <li role="none">
                  <Link
                    className="text-[15px] xl:text-[17px] font-medium text-[#101536] dark:text-slate-200 hover:text-[#1d4ed8] dark:hover:text-blue-400 transition-colors duration-200 no-underline whitespace-nowrap"
                    to="/"
                    role="menuitem"
                    aria-current={window.location.pathname === '/' ? 'page' : undefined}
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  >
                    Home
                  </Link>
                </li>

                <li
                  className="relative"
                  role="none"
                  onMouseEnter={() => setShowDropdownServices(true)}
                  onMouseLeave={() => setShowDropdownServices(false)}
                >
                  <Link
                    className="flex items-center gap-1 text-[15px] xl:text-[17px] font-medium text-[#101536] dark:text-slate-200 hover:text-[#1d4ed8] dark:hover:text-blue-400 transition-colors duration-200 no-underline whitespace-nowrap"
                    to="#"
                    id="servicesDropdown"
                    role="menuitem"
                    aria-haspopup="true"
                    aria-expanded={showDropdownServices}
                    onClick={(e) => {
                      e.preventDefault();
                      setShowDropdownServices(!showDropdownServices);
                    }}
                  >
                    Services
                    <svg className={`w-4 h-4 transition-transform duration-200 ${showDropdownServices ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </Link>
                  <ul
                    id="services-menu"
                    className={`absolute left-1/2 -translate-x-1/2 top-full mt-3 min-w-[280px] max-w-[90vw] rounded-[26px] border border-[#e2e8f0] dark:border-white/5 bg-white dark:bg-[#161D30] shadow-[0_22px_40px_rgba(15,23,42,0.18)] px-4 py-4 transition-all duration-200 ${showDropdownServices ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-3"}`}
                    role="menu"
                    aria-labelledby="servicesDropdown"
                  >
                    {servicesItems.map((item, index) => (
                      <li key={index} role="none">
                        <Link
                          className="flex items-center gap-3 rounded-2xl px-3 py-2.5 text-[#131738] dark:text-slate-200 text-[15px] transition-all duration-200 no-underline hover:bg-[#eff6ff] dark:hover:bg-[#1C253B]"
                          to={item.path}
                          role="menuitem"
                          onClick={() => setShowDropdownServices(false)}
                        >
                          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#edf4ff] dark:bg-blue-950/40 text-[#2563eb] dark:text-blue-400">
                            <item.icon aria-hidden="true" />
                          </span>
                          <span className="font-medium leading-snug whitespace-nowrap">{item.label}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>

                {/* Industry Dropdown - Desktop */}
                <li
                  className="relative"
                  role="none"
                  onMouseEnter={() => setShowDropdownIndustry(true)}
                  onMouseLeave={() => setShowDropdownIndustry(false)}
                >
                  <Link
                    className="flex items-center gap-1 text-[15px] xl:text-[17px] font-medium text-[#101536] dark:text-slate-200 hover:text-[#1d4ed8] dark:hover:text-blue-400 transition-colors duration-200 no-underline whitespace-nowrap"
                    to="#"
                    id="industryDropdown"
                    role="menuitem"
                    aria-haspopup="true"
                    aria-expanded={showDropdownIndustry}
                    onClick={(e) => {
                      e.preventDefault();
                      setShowDropdownIndustry(!showDropdownIndustry);
                    }}
                  >
                    Industry
                    <svg
                      className={`w-4 h-4 transition-transform duration-200 ${showDropdownIndustry ? "rotate-180" : ""
                        }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </Link>
                  <ul
                    id="industry-menu"
                    className={`absolute left-1/2 -translate-x-1/2 top-full mt-3 min-w-[280px] max-w-[90vw] rounded-[26px] border border-[#e2e8f0] dark:border-white/5 bg-white dark:bg-[#161D30] shadow-[0_22px_40px_rgba(15,23,42,0.18)] px-4 py-4 transition-all duration-200 ${showDropdownIndustry
                      ? "opacity-100 visible translate-y-0"
                      : "opacity-0 invisible -translate-y-3"
                      }`}
                    role="menu"
                    aria-labelledby="industryDropdown"
                  >
                    {industryItems.map((item, index) => (
                      <li key={index} role="none">
                        <Link
                          className="flex items-center gap-3 rounded-2xl px-3 py-2.5 text-[#131738] dark:text-slate-200 text-[15px] transition-all duration-200 no-underline hover:bg-[#eff6ff] dark:hover:bg-[#1C253B]"
                          to={item.path}
                          role="menuitem"
                          onClick={() => setShowDropdownIndustry(false)}
                        >
                          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#edf4ff] dark:bg-blue-950/40 text-[#2563eb] dark:text-blue-400">
                            <item.icon aria-hidden="true" />
                          </span>
                          <span className="font-medium leading-snug whitespace-nowrap">
                            {item.label}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>

                {pricingLink.show && (
                  <li role="none">
                    <Link
                      className="text-[15px] xl:text-[17px] font-medium text-[#101536] dark:text-slate-200 hover:text-[#1d4ed8] dark:hover:text-blue-400 transition-colors duration-200 no-underline whitespace-nowrap"
                      to={pricingLink.path}
                      role="menuitem"
                      aria-current={window.location.pathname === pricingLink.path ? 'page' : undefined}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    >
                      {pricingLink.label}
                    </Link>
                  </li>
                )}

                {whyLink.show && (
                  <li role="none">
                    <Link
                      className="text-[15px] xl:text-[17px] font-medium text-[#101536] dark:text-slate-200 hover:text-[#1d4ed8] dark:hover:text-blue-400 transition-colors duration-200 no-underline whitespace-nowrap"
                      to={whyLink.path}
                      role="menuitem"
                      aria-current={window.location.pathname === whyLink.path ? 'page' : undefined}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    >
                      {whyLink.label}
                    </Link>
                  </li>
                )}

                {blogLink.show && (
                  <li role="none">
                    <Link
                      className="text-[15px] xl:text-[17px] font-medium text-[#101536] dark:text-slate-200 hover:text-[#1d4ed8] dark:hover:text-blue-400 transition-colors duration-200 no-underline whitespace-nowrap"
                      to={blogLink.path}
                      role="menuitem"
                      aria-current={window.location.pathname.startsWith(blogLink.path) ? 'page' : undefined}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    >
                      {blogLink.label}
                    </Link>
                  </li>
                )}
              </ul>

              <div className="flex items-center flex-shrink-0 ml-2 xl:ml-4" role="navigation" aria-label="Account Actions">
                <button
                  onClick={toggleTheme}
                  className="relative w-14 h-7 bg-gray-100 dark:bg-[#1E293B] rounded-full p-1 cursor-pointer transition-colors duration-300 focus:outline-none border border-gray-200 dark:border-white/10 flex items-center mr-2 xl:mr-4 select-none"
                  aria-label="Toggle Theme"
                >
                  <div
                    className={`w-5 h-5 rounded-full shadow-md transform transition-transform duration-300 flex items-center justify-center ${
                      theme === "dark" ? "translate-x-[26px] bg-blue-500 text-white" : "translate-x-0 bg-white text-yellow-500"
                    }`}
                  >
                    {theme === "dark" ? (
                      <FaMoon className="w-3 h-3" />
                    ) : (
                      <FaSun className="w-3.5 h-3.5" />
                    )}
                  </div>
                </button>
                {getInTouchLink.show && (
                  <button
                    className="bg-gradient-to-r from-[#ff6b00] to-[#e65c00] text-white font-bold px-3 py-1.5 xl:px-4 xl:py-2 text-sm xl:text-base rounded-md mr-1 xl:mr-2 hover:from-[#ff7b1a] hover:to-[#f06000] transition-all duration-300 whitespace-nowrap shadow-[0_4px_12px_rgba(255,107,0,0.2)] hover:shadow-[0_6px_18px_rgba(255,107,0,0.35)] hover:scale-[1.02]"
                    onClick={handleGetStartedClick}
                    aria-label="Contact Us"
                  >
                    {getInTouchLink.label}
                  </button>
                )}
                {loginLink.show && (
                  <button
                    className="bg-[#007bff] text-white font-bold px-3 py-1.5 xl:px-4 xl:py-2 text-sm xl:text-base rounded-md ml-1 xl:ml-2 hover:bg-[#0056b3] transition-all duration-300 whitespace-nowrap"
                    onClick={() => {
                      window.location.href = `${process.env.REACT_APP_REDIRECT_URL}/login`;
                    }}
                    aria-label="Login to your account"
                  >
                    {loginLink.label}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Overlay */}
        {mobileMenuOpen && (
          <div
            className="fixed inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm z-[1040] lg:hidden"
            onClick={closeMobileMenu}
          />
        )}

        {/* Mobile Navigation Menu */}
        <div
          className={`fixed top-0 right-0 w-[300px] h-screen bg-gradient-to-b from-white to-[#f8fafc] dark:from-[#161D30] dark:to-[#0B0F19] shadow-[-8px_0_30px_rgba(0,0,0,0.15)] z-[1050] transform transition-transform duration-300 ease-out lg:hidden overflow-y-auto ${mobileMenuOpen ? "translate-x-0" : "translate-x-full"}`}
          role="navigation"
          aria-label="Mobile Menu"
        >
          {/* Header with Logo and Close */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#e2e8f0] dark:border-white/10">
            <img src={theme === "dark" ? logoDarkUrl : logoUrl} alt={logoAlt} className="h-[40px] w-auto object-contain" height="40" />
            <div className="flex items-center gap-2">
              <button
                className="flex items-center justify-center w-10 h-10 rounded-full bg-[#f1f5f9] dark:bg-slate-800 text-[#131738] dark:text-slate-200 hover:bg-[#e2e8f0] dark:hover:bg-slate-700 transition-colors duration-200"
                onClick={closeMobileMenu}
                aria-label="Close main menu"
              >
                <FaTimes aria-hidden="true" />
              </button>
            </div>
          </div>

          <ul className="flex flex-col mt-4 px-4" role="menubar" aria-label="Mobile Menu">
            <li className="mb-1" role="none">
              <Link
                className="flex items-center gap-3 py-3 px-4 rounded-xl text-[#131738] dark:text-slate-200 font-medium text-[16px] hover:bg-[#eff6ff] dark:hover:bg-[#1C253B] hover:text-[#007bff] dark:hover:text-blue-400 transition-all duration-200 no-underline"
                to="/"
                role="menuitem"
                onClick={() => {
                  closeMobileMenu();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                <span className="flex items-center justify-center w-9 h-9 rounded-full bg-[#eff6ff] dark:bg-blue-950/40 text-[#007bff] dark:text-blue-400">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
                </span>
                Home
              </Link>
            </li>

            <li className="mb-1" role="none">
              <button
                className="w-full flex items-center gap-3 py-3 px-4 rounded-xl text-[#131738] dark:text-slate-200 font-medium text-[16px] hover:bg-[#eff6ff] dark:hover:bg-[#1C253B] hover:text-[#007bff] dark:hover:text-blue-400 transition-all duration-200 bg-transparent border-0"
                onClick={() => setShowDropdownServices(!showDropdownServices)}
                aria-expanded={showDropdownServices}
              >
                <span className="flex items-center justify-center w-9 h-9 rounded-full bg-[#eff6ff] dark:bg-blue-950/40 text-[#007bff] dark:text-blue-400">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                </span>
                <span className="flex-1 text-left">Services</span>
                <svg className={`w-4 h-4 transition-transform duration-300 ${showDropdownServices ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <ul className={`ml-12 mt-2 rounded-xl border border-[#e0e7ff] dark:border-white/10 bg-white/80 dark:bg-[#161D30]/80 backdrop-blur overflow-hidden transition-all duration-300 ${showDropdownServices ? 'max-h-[420px] opacity-100 py-2' : 'max-h-0 opacity-0 py-0'}`}>
                {servicesItems.map((item, index) => (
                  <li key={index} role="none">
                    <Link
                      className="flex items-center gap-3 py-2.5 px-3 text-[#131738] dark:text-slate-200 text-[14px] transition-all duration-200 no-underline hover:text-[#007bff] dark:hover:text-blue-400 rounded-lg hover:bg-[#eff6ff] dark:hover:bg-[#1C253B]"
                      to={item.path}
                      role="menuitem"
                      onClick={() => {
                        closeMobileMenu();
                        setShowDropdownServices(false);
                      }}
                    >
                      <item.icon className="text-[#007bff] dark:text-blue-400" aria-hidden="true" />
                      <span>{item.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </li>

            {/* Industry - Mobile */}
            <li className="mb-1" role="none">
              <button
                className="w-full flex items-center gap-3 py-3 px-4 rounded-xl text-[#131738] dark:text-slate-200 font-medium text-[16px] hover:bg-[#eff6ff] dark:hover:bg-[#1C253B] hover:text-[#007bff] dark:hover:text-blue-400 transition-all duration-200 bg-transparent border-0"
                onClick={() => setShowDropdownIndustry(!showDropdownIndustry)}
                aria-expanded={showDropdownIndustry}
              >
                <span className="flex items-center justify-center w-9 h-9 rounded-full bg-[#eff6ff] dark:bg-blue-950/40 text-[#007bff] dark:text-blue-400">
                  <FaBuilding aria-hidden="true" />
                </span>
                <span className="flex-1 text-left">Industry</span>
                <svg
                  className={`w-4 h-4 transition-transform duration-300 ${showDropdownIndustry ? "rotate-180" : ""
                    }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              <ul
                className={`ml-12 mt-2 rounded-xl border border-[#e0e7ff] dark:border-white/10 bg-white/80 dark:bg-[#161D30]/80 backdrop-blur overflow-hidden transition-all duration-300 ${showDropdownIndustry
                  ? "max-h-[420px] opacity-100 py-2"
                  : "max-h-0 opacity-0 py-0"
                  }`}
              >
                {industryItems.map((item, index) => (
                  <li key={index} role="none">
                    <Link
                      className="flex items-center gap-3 py-2.5 px-3 text-[#131738] dark:text-slate-200 text-[14px] transition-all duration-200 no-underline hover:text-[#007bff] dark:hover:text-blue-400 rounded-lg hover:bg-[#eff6ff] dark:hover:bg-[#1C253B]"
                      to={item.path}
                      role="menuitem"
                      onClick={() => {
                        closeMobileMenu();
                        setShowDropdownIndustry(false);
                      }}
                    >
                      <item.icon className="text-[#007bff] dark:text-blue-400" aria-hidden="true" />
                      <span>{item.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </li>

            {pricingLink.show && (
              <li className="mb-1" role="none">
                <Link
                  className="flex items-center gap-3 py-3 px-4 rounded-xl text-[#131738] dark:text-slate-200 font-medium text-[16px] hover:bg-[#eff6ff] dark:hover:bg-[#1C253B] hover:text-[#007bff] dark:hover:text-blue-400 transition-all duration-200 no-underline"
                  to={pricingLink.path}
                  role="menuitem"
                  onClick={() => {
                    closeMobileMenu();
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  <span className="flex items-center justify-center w-9 h-9 rounded-full bg-[#eff6ff] dark:bg-blue-950/40 text-[#007bff] dark:text-blue-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  </span>
                  {pricingLink.label}
                </Link>
              </li>
            )}

            {whyLink.show && (
              <li className="mb-1" role="none">
                <Link
                  className="flex items-center gap-3 py-3 px-4 rounded-xl text-[#131738] dark:text-slate-200 font-medium text-[16px] hover:bg-[#eff6ff] dark:hover:bg-[#1C253B] hover:text-[#007bff] dark:hover:text-blue-400 transition-all duration-200 no-underline"
                  to={whyLink.path}
                  role="menuitem"
                  onClick={() => {
                    closeMobileMenu();
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  <span className="flex items-center justify-center w-9 h-9 rounded-full bg-[#eff6ff] dark:bg-blue-950/40 text-[#007bff] dark:text-blue-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" /></svg>
                  </span>
                  {whyLink.label}
                </Link>
              </li>
            )}

            {blogLink.show && (
              <li className="mb-1" role="none">
                <Link
                  className="flex items-center gap-3 py-3 px-4 rounded-xl text-[#131738] dark:text-slate-200 font-medium text-[16px] hover:bg-[#eff6ff] dark:hover:bg-[#1C253B] hover:text-[#007bff] dark:hover:text-blue-400 transition-all duration-200 no-underline"
                  to={blogLink.path}
                  role="menuitem"
                  onClick={() => {
                    closeMobileMenu();
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  <span className="flex items-center justify-center w-9 h-9 rounded-full bg-[#eff6ff] dark:bg-blue-950/40 text-[#007bff] dark:text-blue-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" /></svg>
                  </span>
                  {blogLink.label}
                </Link>
              </li>
            )}
          </ul>

          {/* CTA Buttons */}
          <div className="flex flex-col gap-3 px-4 mt-6 pt-6 border-t border-[#e2e8f0] dark:border-white/10" role="navigation" aria-label="Account Actions">
            {getInTouchLink.show && (
              <button
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#ff6b00] to-[#e65c00] text-white font-semibold px-4 py-3.5 rounded-xl shadow-[0_4px_14px_rgba(255,107,0,0.3)] hover:shadow-[0_6px_20px_rgba(255,107,0,0.45)] transition-all duration-300 hover:from-[#ff7b1a] hover:to-[#f06000]"
                onClick={(e) => {
                  handleGetStartedClick(e);
                  closeMobileMenu();
                }}
                aria-label="Contact Us"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                {getInTouchLink.label}
              </button>
            )}
            {loginLink.show && (
              <button
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#007bff] to-[#0056b3] text-white font-semibold px-4 py-3.5 rounded-xl shadow-[0_4px_14px_rgba(0,123,255,0.4)] hover:shadow-[0_6px_20px_rgba(0,123,255,0.5)] transition-all duration-300"
                onClick={() => {
                  window.location.href = `${process.env.REACT_APP_REDIRECT_URL}/login`;
                  closeMobileMenu();
                }}
                aria-label="Login to your account"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" /></svg>
                {loginLink.label}
              </button>
            )}          </div>
        </div>
      </nav>

      {/* Contact Form Modal */}
      {showContactForm && (
        <ContactForm onClose={handleCloseContactForm} />
      )}
    </>
  );
};
export default NavigationBar;